import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim()
}

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

export function avgRating(reviews: { rating: number }[] | undefined | null): number {
  if (!reviews?.length) return 0
  return reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
}

// Combines written reviews with the archival community poll (vote-weighted),
// so the poll's larger sample is reflected in the same star rating. Falls back
// to the plain review average when no poll data is present.
type RatingSource = {
  reviews?: { rating: number }[] | null
  archivePollScore?: number | null
  archivePollVotes?: number | null
}

export function combinedRating(r: RatingSource): number {
  const reviews = r.reviews ?? []
  const reviewSum = reviews.reduce((s, rv) => s + rv.rating, 0)
  const reviewN = reviews.length
  const pollVotes = r.archivePollVotes ?? 0
  if (r.archivePollScore != null && pollVotes > 0) {
    return (reviewSum + r.archivePollScore * pollVotes) / (reviewN + pollVotes)
  }
  return reviewN ? reviewSum / reviewN : 0
}

// Total number of ratings behind combinedRating (written reviews + poll votes).
export function ratingSampleSize(r: RatingSource): number {
  return (r.reviews?.length ?? 0) + (r.archivePollVotes ?? 0)
}

export function ratingLabel(rating: number): string {
  if (rating >= 4.5) return "Exceptional"
  if (rating >= 4.0) return "Excellent"
  if (rating >= 3.5) return "Very Good"
  if (rating >= 3.0) return "Good"
  if (rating >= 2.0) return "Fair"
  return "Poor"
}

export function ratingColor(rating: number): string {
  if (rating >= 4.5) return "text-emerald-600"
  if (rating >= 4.0) return "text-green-600"
  if (rating >= 3.5) return "text-lime-600"
  if (rating >= 3.0) return "text-yellow-600"
  if (rating >= 2.0) return "text-orange-600"
  return "text-red-600"
}

// ── Location display ──────────────────────────────────────────────────────
// Builds the "City, Region, Country" string shown on cards. The `location`
// field sometimes already contains the country (e.g. archive imports store
// "Paris, France"), which produced duplicates like "Paris, France, France".
// US listings legitimately repeat a name across segments ("New York, New York,
// United States") because the city and state share a name — that's preserved,
// since we only drop the country when it's already one of the location's
// comma-separated segments.
export function formatLocation(location?: string | null, country?: string | null): string {
  const loc = (location ?? "").trim()
  const ctry = (country ?? "").trim()
  if (!loc) return ctry
  if (!ctry) return loc
  const norm = (s: string) => s.toLowerCase().replace(/\./g, "").trim()
  const segments = loc.split(",").map(norm)
  if (norm(loc) === norm(ctry) || segments.includes(norm(ctry))) return loc
  return `${loc}, ${ctry}`
}

// ── Currency / fee display ──────────────────────────────────────────────────
// Fee/cost fields are free text and may already carry a currency indicator
// (€2,500, £35, ¥100, "SEK 15,000", "sliding scale"). Blindly prefixing "$"
// produced "$€2,500" / "$¥100" on cards. This only prepends "$" for a bare
// number with no currency marker. (Future: convert to the viewer's locale —
// for now we render the amount as the program denominated it.)
const CURRENCY_TOKEN_RE =
  /[$€£¥₹₩₪฿₫₴₦]|\b(usd|eur|gbp|jpy|chf|sek|nok|dkk|cad|aud|nzd|inr|cny|rmb|krw|zar|brl|mxn|rub|pln|czk|huf|try|ils|aed|sgd|hkd|thb|php|kr|r\$)\b/i

// Normalize a fee string for display. Returns "No fee" for empty/free/0, the
// value as-written when it already names a currency, and a "$"-prefixed amount
// only for bare numbers. Non-numeric text (e.g. "sliding scale") passes through.
export function formatFeeLabel(fee: string | null | undefined): string | null {
  if (fee == null) return null
  const f = fee.trim()
  if (!f) return null
  if (f === "0" || f.toLowerCase() === "free") return "No fee"
  // Bare number (optionally with thousands separators / decimals) → assume USD.
  if (/^\d[\d,]*(\.\d+)?$/.test(f.replace(/\s/g, ""))) {
    return `$${parseFloat(f.replace(/[^0-9.]/g, "")).toLocaleString()}`
  }
  // Already denominated, or non-numeric descriptive text → leave as written.
  if (CURRENCY_TOKEN_RE.test(f) || !/^\d/.test(f)) return f
  // Leading digit with trailing units but no currency marker → assume USD.
  return `$${f}`
}

// Card-friendly fee: appends " fee" to monetary amounts, leaves descriptive
// text untouched, and truncates to fit a card chip. Values that already say
// "fee" (e.g. "CA$35.00 application fee") are never suffixed again — that
// produced "CA$35.00 application fee fee" on live cards.
export function formatCardFee(fee: string | null | undefined, maxLen = 28): string | null {
  const label = formatFeeLabel(fee)
  if (!label) return null
  if (label === "No fee") return "No fee"
  const display = /\d/.test(label) && !/\bfee\b/i.test(label) ? `${label} fee` : label
  return display.length > maxLen ? display.slice(0, maxLen).trimEnd() + "…" : display
}

export const DISCIPLINES = [
  // Fine Arts
  "Visual Arts",
  "Painting",
  "Sculpture",
  "Drawing",
  "Printmaking",
  "Ceramics",
  "Craft",
  "Installation",
  // Media
  "Photography",
  "Film / Video",
  "Animation",
  "Digital / New Media",
  // Design
  "Design",
  "Illustration",
  "Fashion",
  "Architecture",
  // Performing Arts
  "Performance",
  "Theater",
  "Dance",
  "Music / Sound",
  // Writing
  "Writing / Literature",
  "Poetry",
  "Journalism",
  "Screenwriting",
  // Applied / Conceptual
  "Textiles / Fiber Arts",
  "Curatorial",
  "Social Practice",
  // Business / Technology
  "Technology",
  "Entrepreneurship",
  // Cross-discipline
  "Interdisciplinary",
]

export const CONTINENTS = [
  "North America",
  "South America",
  "Europe",
  "Africa",
  "Asia",
  "Oceania",
]

export const COST_TYPES = [
  { value: "free", label: "Free" },
  { value: "stipend", label: "Paid Stipend" },
  { value: "fee", label: "Program Fee" },
  { value: "self-funded", label: "Self-Funded" },
]

export const APPLICATION_MODES = [
  { value: "open_call", label: "Open Call" },
  { value: "nomination", label: "Nomination Only" },
  { value: "invitation", label: "Invitation Only" },
]

// Imported/legacy listings sometimes carry a costType that isn't one of the
// four canonical values (e.g. "Free", "self_funded", "no cost", or ""). A
// <select> can't display a value that's not in its options, so it silently
// falls back to showing the first option while the form state keeps the bad
// value — which then fails enum validation on save. Normalize to a canonical
// value so what's shown is what's submitted. Unknown/empty → "free" (matches
// the first option the select would otherwise display).
export function normalizeCostType(value: unknown): string {
  if (typeof value !== "string") return "free"
  const v = value.trim().toLowerCase().replace(/[\s_]+/g, "-")
  if (v === "free" || v === "stipend" || v === "fee" || v === "self-funded") return v
  if (["no-cost", "none", "no-charge", "n/a", "na", ""].includes(v)) return "free"
  if (["paid", "funded", "stipended"].includes(v)) return "stipend"
  if (["self-fund", "selffunded", "self"].includes(v)) return "self-funded"
  if (["application-fee", "program-fee", "paid-application"].includes(v)) return "fee"
  return "free"
}

// Same defensive normalization for applicationMode (also rendered via a
// <select>). Unknown/empty → "open_call".
export function normalizeApplicationMode(value: unknown): string {
  if (typeof value !== "string") return "open_call"
  const v = value.trim().toLowerCase().replace(/[\s-]+/g, "_")
  if (v === "open_call" || v === "nomination" || v === "invitation") return v
  if (["open", "opencall", "open_application"].includes(v)) return "open_call"
  if (["nominated", "nomination_only"].includes(v)) return "nomination"
  if (["invite", "invited", "invite_only", "invitation_only", "by_invitation"].includes(v)) return "invitation"
  return "open_call"
}

// Cancellation / refund policy — transparency signal exposed by COVID-era disputes
export const CANCELLATION_POLICIES = [
  { value: "full_refund", label: "Full refund" },
  { value: "partial_refund", label: "Partial refund" },
  { value: "credit_only", label: "Credit / reschedule only" },
  { value: "none", label: "No refund" },
  { value: "not_applicable", label: "Not applicable (no fees)" },
]

export function cancellationPolicyLabel(value?: string | null): string | null {
  if (!value) return null
  return CANCELLATION_POLICIES.find((p) => p.value === value)?.label ?? null
}

// Transparency score: how much useful info does this listing have? (0–100)
export function transparencyScore(residency: {
  applicationFee?: string | null
  programCost?: string | null
  housing?: boolean
  meals?: boolean
  studioSpace?: boolean
  stipend?: string | null
  stipendAmount?: string | null
  costType?: string | null
  duration?: string | null
  website?: string | null
  description?: string | null
  reviews?: { rating: number }[] | null
  claimedByUserId?: string | null
  /** Precomputed isProgramRun() — preferred over claimedByUserId when provided */
  programRun?: boolean
  applicationMode?: string | null
  artistsHostedPerYear?: number | null
  yearFounded?: number | null
  googlePlaceVerifiedAt?: Date | string | null
}): number {
  let score = 0
  const weights = {
    costType: 10,          // basic cost category
    applicationFee: 10,    // explicit fee amount
    programCost: 10,       // total cost disclosure
    amenities: 10,         // housing/meals/studio declared
    duration: 5,           // duration listed
    website: 5,            // website linked
    description: 5,        // has a description > 50 chars
    reviews: 15,           // has at least 1 review
    claimed: 5,            // listing claimed by its owner
    locationVerified: 5,   // location confirmed against Google Maps
    stipend: 10,           // stipend info provided
    applicationMode: 5,    // open call / nomination / invite stated
    artistsHosted: 5,      // artists per year disclosed
  }

  if (residency.costType) score += weights.costType
  if (residency.applicationFee) score += weights.applicationFee
  if (residency.programCost) score += weights.programCost
  if (residency.housing !== undefined || residency.meals !== undefined || residency.studioSpace !== undefined) score += weights.amenities
  if (residency.duration) score += weights.duration
  if (residency.website) score += weights.website
  if (residency.description && residency.description.length > 50) score += weights.description
  if (residency.reviews && residency.reviews.length > 0) score += weights.reviews
  if (residency.programRun ?? residency.claimedByUserId) score += weights.claimed
  if (residency.googlePlaceVerifiedAt) score += weights.locationVerified
  if (residency.stipend || residency.stipendAmount) score += weights.stipend
  if (residency.applicationMode) score += weights.applicationMode
  if (residency.artistsHostedPerYear) score += weights.artistsHosted

  return score
}

export function transparencyLabel(score: number): string {
  if (score >= 80) return "Excellent"
  if (score >= 60) return "Good"
  if (score >= 40) return "Partial"
  return "Limited"
}

export function transparencyColor(score: number): string {
  if (score >= 80) return "text-emerald-600"
  if (score >= 60) return "text-green-600"
  if (score >= 40) return "text-yellow-600"
  return "text-orange-600"
}

// Vibe spectrum matching: Euclidean distance → percentage match
export type VibeSpectrumValues = {
  socialOrbit: number
  creativePace: number
  infrastructure: number
  environment: number
  autonomy: number
}

export function vibeMatchPercentage(a: VibeSpectrumValues, b: VibeSpectrumValues): number {
  const axes: (keyof VibeSpectrumValues)[] = [
    "socialOrbit", "creativePace", "infrastructure", "environment", "autonomy",
  ]
  const sumSqDiff = axes.reduce((sum, axis) => {
    const diff = a[axis] - b[axis]
    return sum + diff * diff
  }, 0)
  // Max possible distance: sqrt(5 * 1^2) ≈ 2.236
  const maxDist = Math.sqrt(axes.length)
  const dist = Math.sqrt(sumSqDiff)
  // Convert to 0–100 percentage (100 = perfect match)
  return Math.round((1 - dist / maxDist) * 100)
}

export function vibeMatchLabel(pct: number): string {
  if (pct >= 90) return "Soul Match"
  if (pct >= 75) return "Strong Match"
  if (pct >= 60) return "Good Match"
  if (pct >= 45) return "Moderate"
  return "Explore"
}

// Teal rating ramp — a single monochromatic spectrum used for residency markers
// on the globe and map. Lower-rated/unrated = light teal, top-rated = deep teal,
// so the color itself encodes rating (no warm accents). Input rating is 0–5.
const TEAL_RAMP = [
  "#99f6e4", // 0–1  light
  "#5eead4", // 1–2
  "#2dd4bf", // 2–3
  "#14b8a6", // 3–4
  "#0d9488", // 4–4.5
  "#0f766e", // 4.5–5 deep
]

export function ratingTeal(rating: number): string {
  if (!rating || rating <= 0) return TEAL_RAMP[0]
  const r = Math.max(0, Math.min(5, rating))
  if (r >= 4.5) return TEAL_RAMP[5]
  if (r >= 4) return TEAL_RAMP[4]
  if (r >= 3) return TEAL_RAMP[3]
  if (r >= 2) return TEAL_RAMP[2]
  if (r >= 1) return TEAL_RAMP[1]
  return TEAL_RAMP[0]
}
